var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import client from 'api/environment';
import ADD_TENANT_CATEGORY from 'api/mutations/ADD_TENANT_CATEGORY';
import REMOVE_TENANT_CATEGORY from 'api/mutations/REMOVE_TENANT_CATEGORY';
import UPDATE_TENANT_CATEGORY from 'api/mutations/UPDATE_TENANT_CATEGORY';
import GET_CURRENT_TENANT_CATEGORIES from 'api/queries/GET_CURRENT_TENANT_CATEGORIES';
const initialState = {
    items: [],
    pageInfo: {
        hasNextPage: true,
        endCursor: null,
    },
    status: {
        fetch: 'idle',
        add: 'idle',
        update: 'idle',
        remove: 'idle',
    },
    errors: {
        fetch: null,
        add: null,
        update: null,
        remove: null,
    },
    refetchFlags: {
        fetch: false,
        add: false,
        update: false,
        remove: false,
    },
};
export const fetchCategories = createAsyncThunk('categories/fetchCategories', (_1, _a) => __awaiter(void 0, [_1, _a], void 0, function* (_, { getState, rejectWithValue }) {
    const state = getState();
    const { pageInfo } = state.parameters.categories;
    try {
        const response = yield client.query({
            query: GET_CURRENT_TENANT_CATEGORIES,
            variables: {
                first: 25,
                after: pageInfo.endCursor,
            },
            fetchPolicy: 'network-only',
        });
        return response.data.me.currentTenant.categories;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const addCategory = createAsyncThunk('categories/addCategory', (_a) => __awaiter(void 0, [_a], void 0, function* ({ tenantId, name }) {
    var _b, _c;
    try {
        const categoryData = yield client.mutate({
            mutation: ADD_TENANT_CATEGORY,
            variables: { tenantId, name, image: '' },
            fetchPolicy: 'no-cache',
        });
        return (_c = (_b = categoryData === null || categoryData === void 0 ? void 0 : categoryData.data) === null || _b === void 0 ? void 0 : _b.addModelSpecificationCategory) === null || _c === void 0 ? void 0 : _c.modelSpecificationCategory;
    }
    catch (error) {
        const apolloError = error;
        return apolloError.message;
    }
}));
export const updateCategory = createAsyncThunk('categories/updateCategory', (_a) => __awaiter(void 0, [_a], void 0, function* ({ categoryId, name }) {
    try {
        const categoryData = yield client.mutate({
            mutation: UPDATE_TENANT_CATEGORY,
            variables: { categoryId, name },
        });
        return categoryData.data.updateCategory.category;
    }
    catch (error) {
        const apolloError = error;
        return apolloError.message;
    }
}));
export const removeCategory = createAsyncThunk('categories/removeCategory', (categoryId) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        yield client.mutate({
            mutation: REMOVE_TENANT_CATEGORY,
            variables: { categoryId },
        });
        return categoryId;
    }
    catch (error) {
        const apolloError = error;
        return apolloError.message;
    }
}));
const categoriesSlice = createSlice({
    name: 'categories',
    initialState,
    reducers: {
        resetCategories(state) {
            state.items = [];
            state.pageInfo = initialState.pageInfo;
            state.status = initialState.status;
            state.errors = initialState.errors;
            state.refetchFlags = initialState.refetchFlags;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCategories.pending, (state) => {
            state.status.fetch = 'loading';
            state.refetchFlags.fetch = false;
        })
            .addCase(fetchCategories.fulfilled, (state, action) => {
            var _a;
            const newCategories = action.payload.edges.map((edge) => edge.node);
            const uniqueCategories = newCategories.filter((newCategory) => !state.items.some((existingCategory) => existingCategory.id === newCategory.id));
            state.items = [...state.items, ...uniqueCategories];
            state.pageInfo = (_a = action.payload) === null || _a === void 0 ? void 0 : _a.pageInfo;
            state.status.fetch = 'succeeded';
        })
            .addCase(fetchCategories.rejected, (state, action) => {
            state.status.fetch = 'failed';
            state.errors.fetch = action.error.message || 'Error fetching components';
        })
            .addCase(addCategory.pending, (state) => {
            state.status.add = 'loading';
        })
            .addCase(addCategory.fulfilled, (state, action) => {
            state.items = [
                ...state.items,
                Object.assign(Object.assign({}, action.payload), { canNameBeUpdated: true, canBeRemoved: true }),
            ];
            state.status.add = 'succeeded';
        })
            .addCase(addCategory.rejected, (state, action) => {
            state.status.add = 'failed';
            state.errors.add = action.error.message || 'Failed to add specification';
        })
            .addCase(updateCategory.pending, (state) => {
            state.status.update = 'loading';
        })
            .addCase(updateCategory.fulfilled, (state, action) => {
            const { id, name } = action.payload;
            const itemIndex = state.items.findIndex((item) => item.id === id);
            if (itemIndex !== -1) {
                state.items[itemIndex] = Object.assign(Object.assign({}, state.items[itemIndex]), { name });
            }
            state.status.update = 'succeeded';
        })
            .addCase(updateCategory.rejected, (state, action) => {
            state.status.update = 'failed';
            state.errors.update = action.error.message || 'Failed to add specification';
        })
            .addCase(removeCategory.pending, (state) => {
            state.status.remove = 'loading';
        })
            .addCase(removeCategory.fulfilled, (state, action) => {
            state.items = state.items.filter((order) => order.id !== action.payload);
            state.status.remove = 'succeeded';
        })
            .addCase(removeCategory.rejected, (state, action) => {
            state.status.remove = 'failed';
            state.errors.remove = action.payload;
        });
    },
});
export const { resetCategories } = categoriesSlice.actions;
export const selectCategories = (state) => state.parameters.categories.items;
export const selectCategoryById = (state, categoryId) => state.parameters.categories.items.find((category) => category.id === categoryId);
export const selectCategoriesPageInfo = (state) => state.parameters.categories.pageInfo;
export const selectCategoriesStatus = (state) => state.parameters.categories.status;
export const selectCategoriesErrors = (state) => state.parameters.categories.errors;
export const selectCategoriesRefetchFlags = (state) => state.parameters.categories.refetchFlags;
export default categoriesSlice.reducer;
